import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { Collapse } from '@mui/material'

import config from '@/config'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton, brandPrimaryButton } from '../_helpers/brandButtons'
import { brandHeading } from '@/utils/brand-typography'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import Badge from '@/modules/base/components/badge/badge'
import { ButtonSquare, CancelButton } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import SepaLogo from '@assets/images/paymentLogos/payments_methods_sepa.png'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { dateConstants } from '@/modules/base/util/constants'
import getSums from '@/modules/moduleSubscription/components/_helpers/getSums'
import getVat1Germany from '@/modules/moduleSubscription/components/_helpers/getVat1Germany'
import { ModuleIconMap } from '@/modules/moduleSubscription/components/list/module-item/module-item'
import getSelectionLines, { SelectionLine } from '../_helpers/getSelectionLines'
import { getAddonIcon } from '../_helpers/addonDisplay'
import type { CompletedOrder } from '../hooks/usePurchase'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    maxWidth: 1120,
    margin: '0 auto',
    color: theme.palette.brand.text.primary,
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  card: {
    padding: 32,
    border: `1px solid ${theme.palette.brand.divider}`,
    borderRadius: theme.borderRadiusExtraLarge,
    background: theme.palette.background.paper,
    [theme.breakpoints.down(768)]: {
      padding: 20,
    },
    boxShadow: '0px 1px 12px 0px #15233F14',
  },
  confirmation: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    textAlign: 'center',
  },
  checkCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 72,
    height: 72,
    borderRadius: theme.borderRadiusPill,
    background: theme.palette.brand.light,
  },
  confirmationTitle: {
    margin: 0,
    ...brandHeading(theme, 'h5'),
    color: theme.palette.brand.text.heading,
  },
  confirmationText: {
    maxWidth: 600,
    margin: 0,
    color: theme.palette.brand.text.secondary,
  },
  pillButton: {
    width: 'auto',
    borderRadius: theme.borderRadiusPill,
  },
  primaryButton: {
    ...brandPrimaryButton(theme),
  },
  outlineButton: {
    ...brandOutlineButton(theme),
  },
  // mobile: "Details anzeigen" sits below the summary
  overviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    [theme.breakpoints.down(768)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 4,
    },
  },
  overviewTitle: {
    margin: 0,
    ...brandHeading(theme, 'h5'),
    color: theme.palette.brand.text.heading,
  },
  overviewSummary: {
    marginTop: 4,
    color: theme.palette.brand.text.secondary,
  },
  toggle: {
    gap: 6,
    whiteSpace: 'nowrap',
    '& svg': {
      transition: theme.transitions.create('transform'),
    },
  },
  toggleOpen: {
    '& svg': {
      transform: 'rotate(180deg)',
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 24,
    paddingTop: 24,
    borderTop: `1px solid ${theme.palette.brand.divider}`,
  },
  planRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 16,
    padding: 24,
    borderRadius: theme.borderRadiusExtraLarge,
    background: theme.backgroundColor,
    [theme.breakpoints.down(768)]: {
      flexDirection: 'column',
      padding: 20,
    },
  },
  planTitle: {
    ...brandHeading(theme, 'h6'),
    color: theme.palette.brand.text.heading,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  planPrice: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    [theme.breakpoints.down(768)]: {
      alignItems: 'flex-start',
    },
  },
  planPriceValue: {
    ...brandHeading(theme, 'h5'),
    color: theme.palette.brand.text.heading,
  },
  strikedPrice: {
    color: theme.palette.brand.text.secondary,
    textDecoration: 'line-through',
  },
  secondaryText: {
    color: theme.palette.brand.text.secondary,
  },
  addonRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    alignItems: 'center',
    gap: 16,
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.palette.brand.divider}`,
    [theme.breakpoints.down(768)]: {
      gridTemplateColumns: '1fr 1fr',
      padding: '16px 0',
    },
    color: theme.palette.brand.text.heading,
  },
  addonTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    fontWeight: theme.brandTypography.weights.semibold,
    [theme.breakpoints.down(768)]: {
      gridColumn: '1 / -1',
    },
  },
  addonIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
  },
  alignRight: {
    textAlign: 'right',
  },
  sums: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    justifyContent: 'end',
    alignItems: 'baseline',
    columnGap: 48,
    rowGap: 12,
    padding: '20px 24px',
    color: theme.palette.brand.text.secondary,
    textAlign: 'right',
    // mobile: the labels move to the left edge, the amounts stay right
    [theme.breakpoints.down(768)]: {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      justifyContent: 'stretch',
      columnGap: 16,
      padding: '16px 0',
      '& > :nth-child(odd)': {
        textAlign: 'left',
      },
    },
  },
  paidLabel: {
    color: theme.palette.brand.text.heading,
    fontWeight: theme.brandTypography.weights.semibold,
  },
  paidAmount: {
    color: theme.palette.brand.main,
    ...brandHeading(theme, 'h5'),
  },
  infoCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 16,
    marginTop: 8,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  infoCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 24,
    borderRadius: theme.borderRadiusExtraLarge,
    background: theme.backgroundColor,
  },
  infoCardTitle: {
    margin: '0 0 8px',
    ...brandHeading(theme, 'h6'),
    color: theme.palette.brand.text.heading,
  },
  infoCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  sepaLogo: {
    alignSelf: 'flex-start',
    height: 20,
  },
  consultation: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '24px 28px',
    [theme.breakpoints.down(768)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: 20,
    },
  },
  consultationButton: {
    [theme.breakpoints.down(768)]: {
      alignSelf: 'stretch',
      width: '100%',
    },
  },
  consultationIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 56,
    height: 56,
    borderRadius: theme.borderRadiusLarge,
    background: theme.palette.brand.light,
  },
  consultationText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  eyebrow: {
    color: theme.palette.brand.main,
    fontSize: theme.brandTypography.text.small.fontSize,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  consultationTitle: {
    fontWeight: theme.brandTypography.weights.semibold,
    color: theme.palette.brand.text.heading,
  },
}))

interface PurchaseSuccessProps {
  order: CompletedOrder
  onFinish: () => void
}

const formatIban = (iban = '') => iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim()

const PurchaseSuccess = ({ order, onFinish }: PurchaseSuccessProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const [showDetails, setShowDetails] = useState(false)
  const { items, period, couponCode, billingData } = order
  const texts = lang.upgrade.wizard.success
  const overviewTexts = texts.overview

  const vat1Germany = useMemo(() => getVat1Germany(), [])
  const lines = useMemo(() => getSelectionLines(items, period), [items, period])
  const sums = useMemo(() => getSums(items, vat1Germany, couponCode, period), [items, vat1Germany, couponCode, period])
  const plans = lines.filter(line => line.isPlan)
  const addons = lines.filter(line => !line.isPlan)
  const planTitle = plans.map(plan => plan.title).join(', ')
  const country = config.countries?.find(({ iso2 }) => iso2 === billingData.countryIso)?.label

  const renderPlan = (plan: SelectionLine) => (
    <div key={plan.id} className={classes.planRow} data-qs-id={`checkout-success-plan-${plan.name}`}>
      <div>
        <div className={classes.planTitle}>{overviewTexts.planTitle(plan.title)}</div>
        <div className={classes.tags}>
          <Badge
            label={period === dateConstants.MONTH ? overviewTexts.tags.monthly : overviewTexts.tags.yearly}
            backgroundColor={theme.palette.brand.light}
            labelColor={theme.palette.brand.main}
          />
          {period === dateConstants.YEAR && (
            <Badge
              label={overviewTexts.tags.minimumTerm(12)}
              backgroundColor={theme.palette.brand.light}
              labelColor={theme.palette.brand.main}
            />
          )}
          {plan.discountPercent && (
            <Badge
              label={overviewTexts.tags.discount(plan.discountPercent, plan.discountMonths)}
              backgroundColor={theme.palette.brand.promotion}
              labelColor={theme.palette.brand.text.primary}
            />
          )}
        </div>
      </div>
      <div className={classes.planPrice}>
        {plan.regularTotal && <span className={classes.strikedPrice}>{formatCurrency(plan.regularTotal)}</span>}
        <span className={classes.planPriceValue} data-typography='heading'>{formatCurrency(plan.total)}</span>
        <span className={classes.secondaryText}>{overviewTexts.perPeriod(period)}</span>
      </div>
    </div>
  )

  const renderAddon = (addon: SelectionLine) => (
    <div key={addon.id} className={classes.addonRow} data-qs-id={`checkout-success-addon-${addon.name}`}>
      <div className={classes.addonTitle}>
        {getAddonIcon(addon.name)
          ? <img className={classes.addonIcon} src={getAddonIcon(addon.name)} alt='' />
          : ModuleIconMap[addon.name] && <SVGIcon svg={ModuleIconMap[addon.name]} width='40px' height='40px' />}
        {addon.title}
      </div>
      <div>
        <div className={classes.secondaryText}>{overviewTexts.count}</div>
        <strong>{addon.count}</strong>
      </div>
      <div className={classes.alignRight}>
        <div className={classes.secondaryText}>{overviewTexts.perPeriod(period)}</div>
        <strong>{formatCurrency(addon.total)}</strong>
      </div>
    </div>
  )

  return (
    <div className={classes.root} data-qs-id='checkout-success'>
      <section className={clsx(classes.card, classes.confirmation)}>
        <div className={classes.checkCircle}>
          <CheckRoundedIcon style={{ fontSize: '28px', color: theme.palette.brand.main }} />
        </div>
        <h2 className={classes.confirmationTitle}>{texts.title}</h2>
        <p className={classes.confirmationText}>{texts.description(planTitle, addons.length)}</p>
        <ButtonSquare className={clsx(classes.pillButton, classes.primaryButton)} label={texts.toDashboard} dataQsId='checkout-success-to-dashboard' onClick={onFinish} />
      </section>

      <section className={classes.card}>
        <div className={classes.overviewHeader}>
          <div>
            <h2 className={classes.overviewTitle}>{overviewTexts.title}</h2>
            <div className={classes.overviewSummary} data-qs-id='checkout-success-summary'>
              {overviewTexts.summary(planTitle, addons.length, formatCurrency(sums.totalGross))}
            </div>
          </div>
          <ButtonSquare
            variant={BUTTON_VARIANTS.link}
            className={clsx(classes.toggle, showDetails && classes.toggleOpen)}
            dataQsId='checkout-success-toggle-details'
            aria-expanded={showDetails}
            onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? overviewTexts.hideDetails : overviewTexts.showDetails}
            <ExpandMoreRoundedIcon style={{ color: theme.palette.brand.main }} />
          </ButtonSquare>
        </div>

        <Collapse in={showDetails} unmountOnExit>
          <div className={classes.details} data-qs-id='checkout-success-details'>
            {plans.map(renderPlan)}
            {addons.map(renderAddon)}
            <div className={classes.sums}>
              <span>{lang.upgrade.wizard.sidebar.subtotal(period)}</span>
              <span>{formatCurrency(sums.totalNet)}</span>
              {couponCode && sums.totalDiscount > 0 && (
                <>
                  <span>{lang.upgrade.wizard.sidebar.couponDiscount(couponCode.code)}</span>
                  <span>{formatCurrency(-sums.totalDiscount)}</span>
                </>
              )}
              <span>{lang.upgrade.wizard.sidebar.vat(sums.vatPercent)}</span>
              <span>{formatCurrency(sums.totalVat)}</span>
              <span className={classes.paidLabel}>{overviewTexts.paid}</span>
              <span className={classes.paidAmount} data-typography='heading'>{formatCurrency(sums.totalGross)}</span>
            </div>
            <div className={classes.infoCards}>
              <div className={classes.infoCard}>
                <h3 className={classes.infoCardTitle}>{overviewTexts.billingAddress}</h3>
                <div className={classes.infoCardContent}>
                  {billingData.companyName && <span>{billingData.companyName}</span>}
                  <span>{[billingData.street, [billingData.zipCode, billingData.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>
                  {country && <span>{country}</span>}
                </div>
              </div>
              <div className={classes.infoCard}>
                <h3 className={classes.infoCardTitle}>{overviewTexts.contact}</h3>
                <div className={classes.infoCardContent}>
                  <span>{[billingData.firstName, billingData.lastName].filter(Boolean).join(' ')}</span>
                  {billingData.phone && <span>{billingData.phone}</span>}
                  {billingData.email && <span>{billingData.email}</span>}
                </div>
              </div>
              {billingData.bankAccountIBAN && (
                <div className={classes.infoCard}>
                  <h3 className={classes.infoCardTitle}>{overviewTexts.paymentMethod}</h3>
                  <img className={classes.sepaLogo} src={SepaLogo} alt={lang.upgrade.wizard.billing.sepa.title} />
                  <div className={classes.infoCardContent}>
                    <span>{overviewTexts.iban(formatIban(billingData.bankAccountIBAN))}</span>
                    {billingData.sepaMandate && <span>{overviewTexts.sepaMandateGranted}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </section>

      <section className={clsx(classes.card, classes.consultation)} data-qs-id='checkout-success-consultation'>
        <div className={classes.consultationIcon}>
          <CalendarTodayRoundedIcon style={{ color: theme.palette.brand.main }} />
        </div>
        <div className={classes.consultationText}>
          <span className={classes.eyebrow}>{texts.consultation.eyebrow}</span>
          <span className={classes.consultationTitle}>{texts.consultation.title}</span>
          <span className={classes.secondaryText}>{texts.consultation.description}</span>
        </div>
        <CancelButton
          className={clsx(classes.pillButton, classes.outlineButton, classes.consultationButton)}
          label={texts.consultation.button}
          dataQsId='checkout-success-book-consultation'
          onClick={() => window.open(config.setupConsultationUrl, '_blank', 'noopener,noreferrer')}
        />
      </section>
    </div>
  )
}

export default PurchaseSuccess
